<article class="agreement-block text-gray-800 leading-relaxed">
  <header class="text-center mb-6">
    <p class="font-bold text-lg">UTP PLAN SUBSCRIBER AGREEMENT</p>
  </header>

  <p class="mb-6">
    The Nasdaq Stock Market LLC (&ldquo;Nasdaq&rdquo;), as Administrator of the UTP Plan
    (&ldquo;UTP Plan Administrator&rdquo;), requires all Subscribers to the information
    described herein (&ldquo;Information&rdquo;) to sign the UTP Plan Subscriber Agreement
    (&ldquo;Agreement&rdquo;), or its equivalent, in order to receive the Information.
    <strong><em>By completing the below section, the Subscriber agrees to the terms and
    conditions set forth in this UTP Plan Subscriber Agreement.</em></strong>
  </p>

  <h3 class="font-bold mb-3">TERMS AND CONDITIONS</h3>
  <p class="font-bold mb-4">
    The Vendor and its agents may not modify or waive any term of this Agreement. Any
    attempt to modify this Agreement, except by Nasdaq, is void.
  </p>

  <ol class="list-decimal pl-8 space-y-4 mb-6">
    <li>
      <strong>USE OF DATA.</strong> Subscriber may not sell, lease, furnish or otherwise
      permit or provide access to the Information to any other Person or to any other office
      or place. Subscriber will not engage in the operation of any illegal business use or
      permit anyone else to use the Information, or any part thereof, for any illegal purpose
      or violate any Nasdaq or Securities and Exchange Commission (&ldquo;SEC&rdquo;)
      Rule or other applicable law, rule or regulation. Subscriber may not present the
      Information rendered in any unfair, misleading or discriminatory format. Subscriber
      shall take reasonable security precautions to prevent unauthorized Persons from
      gaining access to the Information.
      <ol class="list-[lower-alpha] pl-6 mt-3 space-y-3">
        <li>
          <strong>NON-PROFESSIONAL SUBSCRIBER</strong> &mdash; For Non-Professional
          Subscribers, the Information is licensed only for personal use. By representing to
          Vendor that Subscriber is a Non-Professional Subscriber, or by continuing to receive
          the Information at a Non-Professional Subscriber rate, Subscriber is affirming to
          Vendor and to Nasdaq that Subscriber meets the definition of Non-Professional
          Subscriber as set forth in Section 16 of this Agreement. A Non-Professional
          Subscriber shall comply promptly with any reasonable request from Nasdaq for
          information regarding the Non-Professional Subscriber&rsquo;s receipt, processing,
          display and redistribution of the Information.
        </li>
        <li>
          <strong>PROFESSIONAL SUBSCRIBER</strong> &mdash; For Professional Subscribers,
          the Information is licensed for the internal business use and/or personal use of the
          Professional Subscriber. Professional Subscribers may, on a non-continuous basis,
          furnish limited amounts of the Information to customers in written advertisements,
          correspondence or other literature or during voice telephonic conversations not
          entailing computerized voice, automated information inquiry systems or similar
          technologies. Upon request, Professional Subscribers shall make its premises
          available to Nasdaq for physical inspection of Vendor&rsquo;s Service and of
          Professional Subscriber&rsquo;s use of the Information (including review of any
          records regarding use of or access to the Information and the number and locations
          of all devices that receive Information), all at reasonable times, upon reasonable
          notice, to ensure compliance with this Agreement.
        </li>
      </ol>
    </li>
    <li>
      <strong>PROPRIETARY DATA.</strong> Nasdaq grants to Subscriber a nonexclusive,
      non-transferable license during the term of the Agreement to receive and use the
      Information transmitted to it by Vendor and thereafter to use such Information as
      permitted under the terms of this Agreement and/or the UTP Plan Requirements.
      Subscriber acknowledges and agrees that Nasdaq has proprietary rights to the
      Information that originates on or derives from markets regulated or operated by
      Nasdaq, and compilation or other rights to Information gathered from other sources.
      Subscriber further acknowledges and agrees that Nasdaq&rsquo;s third-party
      information providers have exclusive proprietary rights to their respective Information.
      In the event of any misappropriation or misuse by Subscriber or anyone who accesses
      the Information through Subscriber, Nasdaq or its third-party information providers
      shall have the right to obtain injunctive relief for its respective materials. Subscriber
      will attribute source as appropriate under all the circumstances.
    </li>
    <li>
      <strong>PAYMENT.</strong> Subscriber shall assume full and complete responsibility for
      the payment of any taxes, charges or assessments imposed on Subscriber or Nasdaq
      (except for federal, state or local income taxes, if any, imposed on Nasdaq) by any
      foreign or domestic national, state, provincial or local governmental bodies, or
      subdivisions thereof, and any penalties or interest relating to the provision of the
      Information to Subscriber. Interest shall be due from the date of the invoice to the
      time that the amount(s) that are due have been paid. To the extent permitted by
      applicable law, Subscriber acknowledges and agrees that the termination of the
      Vendor&rsquo;s Service for failure to make payments shall not be considered an
      improper limitation of access by Nasdaq. For Professional Subscribers, if any payment
      is due directly to Nasdaq under this Agreement, payment in full is due Nasdaq in
      immediately available funds, in US Dollars by a check to Nasdaq, by electronic funds
      transfer to an institution of Nasdaq&rsquo;s choosing, within fifteen (15) days of the
      date of an invoice, whether or not use is made of, or access is made to, the
      Information.
    </li>
    <li>
      <strong>SYSTEM.</strong> Subscriber acknowledges that Nasdaq, in its sole discretion,
      may from time-to-time make modifications to its system or the Information. Such
      modifications may require corresponding changes to be made in Vendor&rsquo;s
      Service. Changes or the failure to make timely changes by Vendor or Subscriber may
      sever or affect Subscriber&rsquo;s access to or use of the Information. Nasdaq shall
      not be responsible for such effects. Nasdaq does not endorse or approve any
      equipment, Vendor or Vendor&rsquo;s Service.
    </li>
    <li>
      <strong>EXCLUSIVE REMEDY.</strong> Nasdaq shall endeavor to offer the Information as
      promptly and accurately as is reasonably practicable. In the event that the
      Information is not available as a result of failure by Nasdaq to perform its obligations
      under this Agreement, Nasdaq will endeavor to correct any such failure. If the
      Information is not available, is delayed, is interrupted, is incomplete, is not accurate
      or is otherwise materially affected for a continuous period of four (4) hours or more
      during the time that Nasdaq regularly transmits the Information due to the fault of
      Nasdaq (except for a reason permitted in this Agreement or in Nasdaq&rsquo;s
      agreement with the Vendor), Subscriber&rsquo;s or any other Person&rsquo;s exclusive
      remedy against Nasdaq shall be:
      <ol class="list-[lower-alpha] pl-6 mt-3 space-y-3">
        <li>
          If Subscriber or any other Person continues to receive the Information or any
          other data and/or information offered by Nasdaq, a prorated month&rsquo;s
          <u>credit</u> of any monies due for the affected Information directly to Nasdaq from
          Subscriber or, if applicable, from said other Person, for the period at issue; or
        </li>
        <li>
          If Subscriber or any other Person no longer receives either the Information or any
          other data and/or information offered by Nasdaq, a prorated month&rsquo;s
          <u>refund</u> of any monies due for the affected Information directly to Nasdaq
          from Subscriber or, if applicable, from said other Person, for the period at issue.
        </li>
      </ol>
      <p class="mt-3">
        Such credit or refund shall, if applicable, be requested in writing to Nasdaq with all
        pertinent details. Beyond the warranties stated in this section, there are no other
        warranties of any kind &mdash; express, implied, statutory (including without
        limitation, timeliness, truthfulness, sequence, completeness, accuracy, freedom from
        interruption), implied warranties arising from trade usage, course of dealing, course
        of performance or the implied warranties of merchantability or fitness for a particular
        use or purpose.
      </p>
    </li>
    <li>
      <strong>LIMITATION OF LIABILITY.</strong>
      <ol class="list-[lower-alpha] pl-6 mt-3 space-y-3">
        <li>
          Except as may otherwise be set forth herein, Nasdaq shall not be liable to
          Subscriber, its Vendor or any other Person for indirect, special, punitive,
          consequential or incidental loss or damage (including, but not limited to, trading
          losses, loss of anticipated profits, loss by reason of shutdown in operation or
          increased expenses of operation, cost of cover or other indirect loss or damage) of
          any nature arising from any cause whatsoever, even if Nasdaq has been advised
          of the possibility of such damages.
        </li>
        <li>
          Nasdaq shall not be liable to Subscriber or any other Person for any unavailability,
          interruption, delay, incompleteness or inaccuracy of the Information that lasts less
          than four (4) continuous hours during the time that Nasdaq regularly transmits the
          Information or if the Information is materially affected for less than four (4)
          continuous hours during the time that Nasdaq regularly transmits the Information.
        </li>
        <li>
          If Nasdaq is, for any reason, held liable to Subscriber or to any other Person,
          whether in tort or in contract, the liability of Nasdaq within a single year of the
          Agreement (one year from the effective data of the Agreement) is limited to an
          amount of Subscriber&rsquo;s damages that are actually incurred by Subscriber in
          reasonable reliance (combined with the total of all claims or losses of
          Subscriber&rsquo;s Vendor and any other Person claiming through, on behalf of or
          as harmed by Subscriber) and which amount does not exceed the lesser of:
          <ol class="list-[lower-roman] pl-6 mt-2 space-y-2">
            <li>
              For Subscriber or any other person that continues to receive the Information or
              any other data and/or Information offered by Nasdaq, a prorated month&rsquo;s
              <u>credit</u> of any monies due directly to Nasdaq from Subscriber or, if
              applicable, from any other Person, for the Information at issue during the period
              at issue, or if Subscriber or any other Person no longer receives either the
              Information or any other data and/or information offered by Nasdaq, a
              <u>refund</u> of any monies due directly to Nasdaq from Subscriber or, if
              applicable, from any other Person, for the Information at issue during the period
              at issue; or
            </li>
            <li>$500.</li>
          </ol>
        </li>
        <li>
          This section shall not relieve Nasdaq, Subscriber or any other Person from liability
          for damages that result from their own gross negligence or willful tortious
          misconduct or from personal injury or wrongful death claims.
        </li>
        <li>
          Subscriber and Nasdaq understand and agree that the terms of this section reflect
          a reasonable allocation of risk and limitation of liability.
        </li>
      </ol>
    </li>
    <li>
      <strong>DISCLAIMERS OF WARRANTIES.</strong> Nasdaq and its third-party information
      providers make no warranties of any kind &mdash; express, implied or statutory
      (including without limitation, timeliness, truthfulness, sequence, completeness,
      accuracy, freedom from interruption), any implied warranties arising from trade usage,
      course of dealing, course of performance or the implied warranties of merchantability
      or fitness for a particular use or purpose or noninfringement.
    </li>
    <li>
      <strong>THIRD-PARTY INFORMATION PROVIDERS&rsquo; LIMITATION OF LIABILITY.</strong>
      Nasdaq&rsquo;s third-party information providers shall have no liability for any
      damages for the accuracy of or for delays or omissions in any of the Information
      provided by them, whether direct or indirect, lost profits, special or consequential
      damages of the Subscriber or any other Person seeking relief through Subscriber, even
      if the third-party information providers have been advised of the possibility of such
      damages. In no event will the liability of the third-party information providers or their
      affiliates to Subscriber or any other Person seeking relief through Subscriber pursuant
      to any cause of action, whether in contract, tort or otherwise, exceed the fee paid by
      Subscriber or any other Person seeking relief through Subscriber, as applicable.
    </li>
    <li>
      <strong>CLAIMS AND LOSSES.</strong> Subscriber will indemnify Nasdaq and hold
      Nasdaq and its employees, officers, directors and other agents harmless from any and
      all Claims or Losses imposed on, incurred by or asserted as a result of or relating to:
      (a) any noncompliance by Subscriber with the terms and conditions hereof; (b) any
      third-party actions related to Subscriber&rsquo;s receipt and use of the Information,
      whether authorized or unauthorized under the Agreement. Each party warrants and
      represents and will indemnify and hold harmless (and in every case, Nasdaq shall be
      permitted to solely defend and settle) another party (including Nasdaq) and their
      officers, directors, employees and other agents, against any Claims or Losses arising
      from, involving or relating to a claim of infringement or other violation of an
      intellectual property right by the indemnifying party, its actions or omissions,
      equipment or other property. This right is conditioned on the indemnified party giving
      prompt written notice to the indemnifying party (as does not prejudice the defense) of
      the Claims or Losses and providing cooperation in the defense of the Claims or Losses
      (without waiver of attorney-client, work-product or other legal privilege, or disclosure
      of information legally required to be kept confidential).
    </li>
    <li>
      <strong>PERSONAL DATA.</strong> Subscriber acknowledges that Nasdaq, in the course
      of providing services to Subscriber, may process Personal Data (as defined in the The
      Nasdaq Stock Market LLC Vendor Agreement for UTP Plan Services (&ldquo;Vendor
      Agreement&rdquo;)) in the performance of services or in support of its rights (including,
      but not limited to, its audit and usage review rights) under the Vendor Agreement.
      Subscriber shall provide to Vendor or its designee such Personal Data (including, but
      not limited to, information regarding Subscriber or, for Subscribers that are firms,
      information regarding individual users of the Information) as reasonably requested by
      Nasdaq to make Information available to Subscriber, perform Nasdaq&rsquo;s services
      under the Vendor Agreement, and/or enforce Nasdaq&rsquo;s rights (including, but not
      limited to, its audit and usage review rights) under the Vendor Agreement, and
      Vendor shall provide such information to Nasdaq or its designee. Provisions for the
      processing of such data are set forth in the Vendor Agreement. With respect to
      individuals whose Personal Data is processed by Nasdaq and/or its service providers,
      the current publicly-posted Privacy Policy identified on the UTP Plan website located at
      <a href="https://www.utpplan.com" target="_blank" rel="noopener" class="text-blue-600 hover:underline">www.utpplan.com</a>,
      or its successor website, shall apply to such processing. To the extent that the
      Subscriber is a legal entity established in the European Economic Area
      (&ldquo;EEA&rdquo;), transfers of Personal Data to a Vendor (or its designee) outside
      of the EEA in connection with this Agreement shall be governed by the Data
      Processing Addendum of the Vendor Agreement, which is incorporated herein by
      reference <em>mutatis mutandis</em>, with the personal data exporter being the
      Subscriber and the personal data importer being the Vendor. Transfers of personal
      data from Vendor to Nasdaq shall be governed by the relevant provisions of the
      Vendor Agreement.
    </li>
    <li>
      <strong>TERMINATION.</strong> Subscriber acknowledges that Nasdaq, when required to
      do so in fulfillment of statutory obligations, may by notice to Vendor unilaterally limit
      or terminate the right of any or all Persons to receive or use the Information and that
      Vendor will immediately comply with any such notice and will terminate or limit the
      furnishing of the Information and confirm such compliance by notice to Nasdaq. Any
      affected Person will have available to it such procedural protections as are provided
      by the Act and applicable rules thereunder. In addition to terminations permitted
      under the Vendor&rsquo;s agreement, this Agreement may be terminated by Subscriber
      with thirty (30) days written notice to Vendor and by Nasdaq with thirty (30) days
      written notice either to Vendor or Subscriber. Nasdaq may also alter any term of this
      Agreement with ninety (90) days written notice either to Vendor or Subscriber, and
      any use after such date is deemed acceptance of the new terms. In the event of
      Subscriber breach, discovery of the untruth of any representation of Subscriber, or
      where directed by the SEC in its regulatory authority, Nasdaq may terminate this
      Agreement with not less than three (3) days written notice to Subscriber provided
      either by Nasdaq or Vendor.
    </li>
    <li>
      <strong>AMENDMENTS/AGREEMENT.</strong> Except as otherwise provided herein, no
      provision of this Agreement may be amended, modified or waived. No failure on the
      part of Nasdaq or Subscriber to exercise, no delay in exercising and no course of
      dealing with respect to any right, power or privilege under this Agreement shall
      operate as a waiver thereof, nor shall any single or partial exercise of any such right,
      power or privilege preclude any other or further exercise thereof or the exercise of
      any other right, power or privilege under this Agreement. If any of the provisions of
      this Agreement or application thereof to any individual, entity or circumstance is held
      invalid or unenforceable, the remainder of this Agreement, or the application of such
      terms or provisions to individuals, entities or circumstances other than those as to
      which they are held invalid or unenforceable, shall not be affected thereby and each
      such term and provision of this Agreement shall be valid and enforceable to the fullest
      extent permitted by law. In the event of any conflict between the terms of this
      Agreement and of the Vendor&rsquo;s agreement, the terms of this Agreement shall
      prevail as between Nasdaq and Subscriber.
    </li>
    <li>
      <strong>REQUIREMENTS OF SELF-REGULATORY ORGANIZATION; ACTIONS TO BE
      TAKEN IN FULFILLMENT OF STATUTORY OBLIGATIONS.</strong>
      <p class="mt-3">
        (a) Subscriber acknowledges that in the United States: (i) Nasdaq is registered with
        the SEC as national securities exchanges pursuant to Section 6 of the Act, and FINRA
        is registered with the SEC as a national securities association pursuant to 15A of the
        Act; (ii) FINRA and Nasdaq have a statutory obligation to protect investors and the
        public interest, and to ensure that quotation information supplied to investors and
        the public is fair and informative, and not discriminatory, fictitious or misleading;
        (iii) Section 19(g)(1) of the Act mandates that FINRA and Nasdaq comply with the
        UTP Plan Requirements; (iv) Nasdaq has jurisdiction to enforce compliance with
        certain of the UTP Plan Requirements; (v) FINRA has jurisdiction to enforce
        compliance with certain of the UTP Plan Requirements; and (vi) Nasdaq is obligated
        to offer terms that are not unreasonably discriminatory between Subscribers,
        subject to applicable UTP Plan Requirements. Accordingly, Subscriber agrees that
        Nasdaq, when required to do so in fulfillment of its statutory obligations, may,
        temporarily or permanently, unilaterally condition, modify or terminate the right of
        any or all individuals or entities to receive or use the Information. Nasdaq shall
        undertake reasonable efforts to notify Subscriber of any such condition, modification
        or termination, and Subscriber shall promptly comply with any such notice within
        such period of time as may be determined in good faith by Nasdaq to be necessary,
        consistent with its statutory obligations. Any Person that receives such a notice shall
        have available to it such procedural protections as are provided to it by the Act and
        the applicable rules thereunder.
      </p>
      <p class="mt-3">
        (b) If Subscriber is a member of a Nasdaq market, then Subscriber expressly
        acknowledges and agrees that (i) this Agreement does not limit or reduce in any
        way Subscriber&rsquo;s obligations and responsibilities as a member of any
        applicable Nasdaq market; (ii) this Agreement does not in any way alter the
        procedures or standards generally applicable to disciplinary or other actions taken
        by Nasdaq to enforce compliance with, or impose sanctions for violations of, the UTP
        Plan Requirements; and (iii) the nonpayment of amounts due under this Agreement
        could result in the suspension or cancellation of Subscriber&rsquo;s membership in a
        Nasdaq market in accordance with the UTP Plan Requirements.
      </p>
    </li>
    <li>
      <strong>GOVERNING LAWS; CONSTRUCTION.</strong> This Agreement shall be construed
      and enforced in accordance with, and the validity and performance hereof shall be
      governed by, the laws of the State of New York, without reference to principles of
      conflicts of laws thereof. Any dispute that cannot be amicably settled that arises out
      of this Agreement shall be referred to arbitration and shall be conducted in
      accordance with the rules of the American Arbitration Association. All such
      proceedings shall be held in New York City, NY, and shall be conducted in the English
      language, which shall also be the language of the documents.
    </li>
    <li>
      <strong>NOTICES; NOTIFICATION OF CHANGES.</strong> All notices and other
      communications (except for invoices) required to be given in writing under this
      Agreement shall be directed to the signatories or, in the alternative, to the individuals
      identified in subsections (a) and (b) below. Notices shall be deemed to have been
      duly given: (i) upon actual receipt (or date of first refusal) by the parties, or (ii) upon
      constructive receipt (or date of first refusal) if sent by certified mail, return receipt
      requested, or any other delivery method that actually obtains a signed delivery
      receipt, to the following addresses or to such other address as any party hereto shall
      hereafter specify by prior written notice to the other party or parties below, or
      (iii) upon posting the notice or other communication on the
      <a href="https://www.utpplan.com" target="_blank" rel="noopener" class="text-blue-600 hover:underline">www.utpplan.com</a>
      website or a successor site. If an email address is provided, Nasdaq may, in lieu of
      the above, give notice to or communicate with Subscriber by email addressed to the
      persons identified in subsection (a) or to such other email address or persons as
      Subscriber shall hereafter specify by prior written notice. By providing an email
      address, Subscriber agrees that any receipt received by Nasdaq from
      Subscriber&rsquo;s service provider or internet computer server indicating that the
      email was received shall be deemed proof that Subscriber received the message. If
      Subscriber cannot see or printout all or any portion of the message, Subscriber agrees
      that it is Subscriber&rsquo;s responsibility to contact Nasdaq at (301) 978&ndash;8080.
      <div class="grid sm:grid-cols-2 gap-6 mt-4 text-sm">
        <div>
          <p class="font-semibold mb-1">(a) If to Subscriber:</p>
          <p>The Subscriber name, title, address, telephone, fax and email address
          provided during electronic acceptance of this Agreement.</p>
          <p class="mt-3 italic">With, in the event of notices of dispute or default, a required
          copy to: the Subscriber&rsquo;s designated counsel.</p>
        </div>
        <div>
          <p class="font-semibold mb-1">(b) If to UTP Plan Administrator:</p>
          <p>UTP Plan Administrator<br>
          805 King Farm Boulevard<br>
          Rockville, MD 20850<br>
          Phone: +1 301 978 8080<br>
          <a href="mailto:admin&#64;utpplan.com" class="text-blue-600 hover:underline">admin&#64;utpplan.com</a></p>
          <p class="mt-3 italic">With, in the event of notices of default or dispute, a required
          copy to:</p>
          <p>The Nasdaq Stock Market, LLC<br>
          Office of General Counsel<br>
          805 King Farm Boulevard<br>
          Rockville, MD 20850</p>
        </div>
      </div>
    </li>
    <li>
      <strong>DEFINITIONS.</strong>
      <dl class="mt-3 space-y-3">
        <div>
          <dt class="inline font-semibold">Act</dt>
          <dd class="inline"> shall mean the Securities Exchange Act of 1934.</dd>
        </div>
        <div>
          <dt class="inline font-semibold">Affiliate</dt>
          <dd class="inline"> shall mean any individual, corporation, company, partnership,
          limited partnership, limited liability company, trust, association or other entity that,
          directly or indirectly through one or more intermediaries, controls, is controlled by,
          or is under common control with such party.</dd>
        </div>
        <div>
          <dt class="inline font-semibold">Claims or Losses</dt>
          <dd class="inline"> &mdash; Any and all liabilities, obligations, losses, damages,
          penalties, claims, actions, suits, costs, judgments, settlements and expenses of
          whatever nature, whether incurred by or issued against an indemnified party or a
          third party, including, without limitation, (a) indirect, special, punitive,
          consequential or incidental loss or damage, (including, but not limited to, trading
          losses, loss of anticipated profits, loss by reason of shutdown in operation or
          increased expenses of operation or other indirect loss or damage), and
          (b) administrative costs, investigatory costs, litigation costs and auditors&rsquo;
          and attorneys&rsquo; fees and disbursements (including in-house personnel).</dd>
        </div>
        <div>
          <dt class="inline font-semibold">Information</dt>
          <dd class="inline"> shall mean certain market data and other data disseminated
          that has been collected, validated, processed, and recorded by the System or
          other sources made available for transmission to and receipt from either a Vendor
          or from Nasdaq relating to: a) eligible securities or other financial instruments,
          markets, products, vehicles, indicators, or devices; b) activities of a Nasdaq
          Company; c) other information and data from a Nasdaq Company. Information also
          includes any element of Information as used or processed in such a way that the
          Information can be identified, recalculated or re-engineered from the processed
          Information or that the processed Information can be used as a substitute for
          Information.</dd>
        </div>
        <div>
          <dt class="inline font-semibold">Nasdaq</dt>
          <dd class="inline"> shall collectively mean The Nasdaq Stock Market LLC, a
          Delaware limited liability company and its subsidiaries and Affiliates (collectively
          &ldquo;Nasdaq&rdquo;).</dd>
        </div>
        <div>
          <dt class="inline font-semibold">UTP Plan Requirements</dt>
          <dd class="inline"> &mdash; All (i) rules, regulations, interpretations, decisions,
          opinions, orders and other requirements of the SEC; (ii) the rules and regulations,
          disciplinary decision and rule interpretations applicable to UTP; (iii) the decisions,
          policies, interpretations, operating procedures, specifications, requirements, and
          other documentation by Nasdaq, as Administrator of the UTP Plan, that is
          regulatory or technical in nature (including, but not limited to, user guides)
          published on the UTP Plan website located at
          <a href="https://www.utpplan.com" target="_blank" rel="noopener" class="text-blue-600 hover:underline">www.utpplan.com</a>
          or another website accessible by and made known to Vendor; and (iv) all other
          applicable laws, statutes, rules, regulations, orders, decisions, interpretations,
          opinions, and other requirements, whether promulgated by the United States or
          any other applicable jurisdiction (including in the area of intellectual property);
          and (v) the successors, as they may exist at the time, of the components of the
          UTP Plan Requirements.</dd>
        </div>
        <div>
          <dt class="inline font-semibold">Or</dt>
          <dd class="inline"> &mdash; Includes the word &ldquo;and.&rdquo;</dd>
        </div>
        <div>
          <dt class="inline font-semibold">Person</dt>
          <dd class="inline"> &mdash; Any natural person, proprietorship, corporation,
          partnership or other entity whatsoever.</dd>
        </div>
        <div>
          <dt class="inline font-semibold">Subscriber</dt>
          <dd class="inline"> &mdash; When it appears alone, the word
          &ldquo;Subscriber&rdquo; encompasses all Non-Professional and Professional
          Subscribers. All Subscribers are deemed Professional unless they are qualified as
          Non-Professional.</dd>
          <ul class="list-disc pl-6 mt-2 space-y-2">
            <li>
              <strong>Non-Professional Subscriber</strong> &mdash; Any natural person who
              is <strong>NOT</strong>:
              <ol class="list-[lower-alpha] pl-6 mt-1 space-y-1">
                <li>
                  registered or qualified in any capacity with the SEC, the Commodities
                  Futures Trading Commission, any state securities agency, any securities
                  exchange or association or any commodities or futures contract market or
                  association;
                </li>
                <li>
                  engaged as an &ldquo;investment adviser&rdquo; as that term is defined in
                  Section 202(a)(11) of the Investment Advisers Act of 1940 (whether or not
                  registered or qualified under that Act); or
                </li>
                <li>
                  employed by a bank or other organization exempt from registration under
                  federal or state securities laws to perform functions that would require
                  registration or qualification if such functions were performed for an
                  organization not so exempt.
                </li>
              </ol>
            </li>
            <li>
              <strong>Professional Subscriber</strong> &mdash; All other persons who do
              <strong>not</strong> meet the definition of Non-Professional Subscriber.
            </li>
          </ul>
        </div>
        <div>
          <dt class="inline font-semibold">System</dt>
          <dd class="inline"> shall mean any system Nasdaq has developed for the creation
          and/or dissemination of Information.</dd>
        </div>
        <div>
          <dt class="inline font-semibold">Vendor</dt>
          <dd class="inline"> shall mean Vendor and its Affiliates as identified in writing to
          Nasdaq.</dd>
        </div>
        <div>
          <dt class="inline font-semibold">Vendor&rsquo;s Service</dt>
          <dd class="inline"> &mdash; The service from a Vendor, including the data
          processing equipment, software and communications facilities related thereto, for
          receiving, processing, transmitting, using and disseminating the Information to or
          by Subscriber.</dd>
        </div>
      </dl>
    </li>
  </ol>

  <p class="text-xs text-gray-500 mt-8">UTP Plan Subscriber Agreement 2019-1</p>
</article>
